import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { IKeyValue } from '../interfaces/IKeyValue';
import { ICreditCard } from '../interfaces/payments/ICreditCard';
import { ICreditCardRequest } from '../interfaces/payments/ICreditCardRequest';
import { ICreditCardResponse } from '../interfaces/payments/ICreditCardResponse';
import { HttpService } from './http/http.service';
import { IECheck } from '../interfaces/payments/IECheck';
import { IECheckRequest } from '../interfaces/payments/IECheckRequest';
import { IECheckResponse } from '../interfaces/payments/IECheckResponse';
import { PaymentStatus } from '../models/enums/PaymentStatus';
import { ICustomerInvoiceOverview } from '../interfaces/payments/ICustomerInvoiceOverview';
import { Response } from '../models/http/response';
import { IDocument } from '../interfaces/payments/IDocument';
import { PaymentsDataService } from './data/payments.data.service';
import { ICustomerInvoiceInfo } from '../interfaces/payments/ICustomerInvoiceInfo';

@Injectable({
  providedIn: 'root'
})
export class PaymentsService {
  private baseUrl = environment.order.baseUrl;
  private apimAPIKey: IKeyValue[] = [
    {
      key: 'Ocp-Apim-Subscription-Key',
      value: environment.order.subscriptionKey
    }
  ];

  constructor(
    private httpService: HttpService,
    private paymentsDataService: PaymentsDataService
  ) {}

  public addCreditCardPayment(
    payment: ICreditCard,
    taxReturnId: string,
    orderId: string,
    anonymous: boolean
  ): Observable<ICreditCardResponse> {
    const routePart = anonymous ? 'verified' : 'mobile';

    const body: ICreditCardRequest = this.setCreditCard(payment, orderId);
    return this.httpService.post<ICreditCardResponse>(
      true,
      `${this.baseUrl}/v1/${routePart}/payment/creditcard/${taxReturnId}`,
      body,
      null,
      this.apimAPIKey
    );
  }

  public addECheckPayment(
    payment: IECheck,
    taxReturnId: string,
    orderId: string,
    anonymous: boolean
  ): Observable<IECheckResponse> {
    const routePart = anonymous ? 'verified' : 'mobile';

    const body: IECheckRequest = this.setECheck(payment, orderId);
    return this.httpService.post<IECheckResponse>(
      true,
      `${this.baseUrl}/v1/${routePart}/payment/echeck/${taxReturnId}`,
      body,
      null,
      this.apimAPIKey
    );
  }

  public getAnonymousInvoiceOverview(
    taxReturnId: string
  ): Observable<Response<ICustomerInvoiceOverview>> {
    return this.httpService
      .get<ICustomerInvoiceOverview>(
        true,
        `${this.baseUrl}/v1/verified/taxreturn/${taxReturnId}/invoice-overview`,
        null,
        null,
        this.apimAPIKey
      )
      .pipe(map((res) => new Response(res)));
  }

  public getCustomerInvoiceOverviews(): Observable<
    Response<ICustomerInvoiceOverview[]>
  > {
    return this.httpService
      .get<ICustomerInvoiceOverview>(
        true,
        `${this.baseUrl}/v1/mobile/invoice/overviews`,
        null,
        null,
        this.apimAPIKey
      )
      .pipe(map((res) => new Response(res)));
  }

  public getCustomerInvoiceInfo(
    taxReturnId: string,
    invoiceId: string
  ): Observable<Response<ICustomerInvoiceInfo[]>> {
    return this.httpService
      .get<ICustomerInvoiceInfo>(
        true,
        `${this.baseUrl}/v1/mobile/invoice/information/${taxReturnId}/${invoiceId}`,
        null,
        null,
        this.apimAPIKey
      )
      .pipe(map((res) => new Response(res)));
  }

  public getCustomerInvoiceDocument(
    orderId: string,
    invoiceId: string,
    anonymous: boolean
  ): Observable<Response<IDocument>> {
    const routePart = anonymous ? 'verified' : 'mobile';
    return this.httpService
      .get<IDocument>(
        true,
        `${this.baseUrl}/v1/${routePart}/invoice/document/${orderId}/${invoiceId}`,
        null,
        null,
        this.apimAPIKey
      )
      .pipe(
        map(
          (res) => new Response(res),
          (error) => console.log(error)
        )
      );
  }

  public getCreditCardReceipt(
    orderId: string,
    paymentId: string,
    anonymous: boolean
  ): Observable<Response<IDocument>> {
    const routePart = anonymous ? 'verified' : 'mobile';
    return this.httpService
      .get<IDocument>(
        true,
        `${this.baseUrl}/v1/${routePart}/receipt/creditcard/${orderId}/${paymentId}`,
        null,
        null,
        this.apimAPIKey
      )
      .pipe(map((res) => new Response(res)));
  }

  public getECheckReceipt(
    orderId: string,
    paymentId: string,
    anonymous: boolean
  ): Observable<Response<IDocument>> {
    const routePart = anonymous ? 'verified' : 'mobile';
    return this.httpService
      .get<IDocument>(
        true,
        `${this.baseUrl}/v1/${routePart}/receipt/echeck/${orderId}/${paymentId}`,
        null,
        null,
        this.apimAPIKey
      )
      .pipe(map((res) => new Response(res)));
  }

  public setCreditCard(
    payment: ICreditCard,
    orderId: string
  ): ICreditCardRequest {
    return {
      orderId: orderId,
      amount: payment.amount,
      notes: payment.notes,
      cardHolderName: payment.cardHolderName,
      cardNumber: payment.cardNumber,
      expirationMonth: payment.expirationMonth,
      expirationYear: payment.expirationYear,
      securityCode: payment.securityCode,
      isSwiped: false,
      billingAddress: {
        line1: payment.billingAddress.line1,
        line2: payment.billingAddress.line2,
        inCareOf: payment.billingAddress.inCareOf,
        apartmentNumber: payment.billingAddress.apartmentNumber,
        city: payment.billingAddress.city,
        zipCode: payment.billingAddress.zipCode,
        state: payment.billingAddress.state,
        country: payment.billingAddress.country,
        foreignProvince: payment.billingAddress.foreignProvince,
        foreignPostalCode: payment.billingAddress.foreignPostalCode
      }
    };
  }

  public setECheck(payment: IECheck, orderId: string): IECheckRequest {
    return {
      orderId: orderId,
      amount: payment.amount,
      notes: payment.notes,
      accountOwnerName: payment.accountOwnerName,
      accountNumber: payment.accountNumber,
      routingNumber: payment.routingNumber,
      billingAddress: {
        line1: payment.billingAddress.line1,
        line2: payment.billingAddress.line2,
        inCareOf: payment.billingAddress.inCareOf,
        apartmentNumber: payment.billingAddress.apartmentNumber,
        city: payment.billingAddress.city,
        zipCode: payment.billingAddress.zipCode,
        state: payment.billingAddress.state,
        country: payment.billingAddress.country,
        foreignProvince: payment.billingAddress.foreignProvince,
        foreignPostalCode: payment.billingAddress.foreignPostalCode
      }
    };
  }
}
